import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './ProductBenefits.css'
import BaseButton from '../../Common/BaseButton';
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useProductContext } from '../../Context/ProductContext';
import { manageAgentSessionIds } from '../../../helpers/index';
import { generateProductDescriptionStream } from '../../../utils/Product';

const ProductBenefit02 = ({ initialData, isLoadedFromDB, language, locationFocus, keywords: propKeywords, agentId: propAgentId, sessionId: propSessionId, previewMode, setError }) => {
  const [headline, setHeadline] = useState("");
  const [benefits, setBenefits] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previousResponse, setPreviousResponse] = useState(null);
  
  const location = useLocation();
  const initialSku = location.state?.sku || "";
  const keywords = propKeywords || location.state?.keywords || [];
  const { agentId: hookAgentId, sessionId: hookSessionId } = manageAgentSessionIds(initialSku);
  const agentId = propAgentId || hookAgentId;
  const sessionId = propSessionId || hookSessionId;
  const componentType = "benefits03";
  const { productData, updateProductData } = useProductContext();

  useEffect(() => {
    const data = productData[initialSku]?.benefits03 || initialData;
    if (data) {
      setHeadline(data.headline || data.title || "");
      setBenefits(data.benefits || data.description || []);
      setPreviousResponse(data);
      setIsLoading(false);
    }
  }, [initialData, productData, initialSku]);

  const handleProductRegenerate = async (section, furtherDetails) => {
    try {
      setIsEditing(false);
      setIsLoading(true);
      setHeadline("");
      setBenefits([]);

      let finalHeadline = "";
      let finalBenefits = [];
      
      const languageValue = language?.label || language || "";
      const locationValue = locationFocus?.label || locationFocus || "";

      await generateProductDescriptionStream(
        initialSku,
        keywords,
        section,
        furtherDetails,
        agentId,
        sessionId,
        (chunk) => {
          // This is called for each streamed chunk
          // Handle the nested data structure: data: data: {"component_name": "benefits4", "value": {...}}
          if (chunk && chunk.data && chunk.data.data) {
            const responseData = chunk.data.data;
            if (responseData.value) {
              finalHeadline = responseData.value.headline || "";
              finalBenefits = responseData.value.benefits || [];
              setHeadline(finalHeadline);
              setBenefits(finalBenefits);
            }
          }
          // Also handle direct value structure for backward compatibility
          else if (chunk && chunk.value) {
            finalHeadline = chunk.value.headline || "";
            finalBenefits = chunk.value.benefits || [];
            setHeadline(finalHeadline);
            setBenefits(finalBenefits);
          }
          // Handle direct headline and benefits for other cases
          else if (chunk && chunk.headline) {
            finalHeadline = chunk.headline;
            setHeadline(finalHeadline);
          }
          else if (chunk && chunk.benefits) {
            finalBenefits = chunk.benefits;
            setBenefits(finalBenefits);
          }
        },
        () => {
          // onComplete - save the final data and capture as previous response
          const finalData = {
            headline: finalHeadline,
            benefits: finalBenefits
          };
          
          // Capture the current response as previous response for next regeneration
          setPreviousResponse(finalData);
          
          updateProductData(initialSku, componentType, finalData);
          setIsLoading(false);
        },
        (err) => {
          setError(err.message || "Streaming error");
          setIsLoading(false);
        },
        previousResponse, // Pass the previous response
        languageValue,
        locationValue
      );
    } catch (error) {
      setError(error.message || "Streaming error");
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    const updatedData = {
      headline,
      benefits
    };
    updateProductData(initialSku, componentType, updatedData);
    setIsEditing(false);
  };
  
  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const onRegenerate = async (formData) => {
    setIsLoading(true);
    await handleProductRegenerate(componentType, formData, previousResponse);
    setIsLoading(false);
  };

  return (
    <div className={`container py-4 ${previewMode ? "" : "product-section"}`}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        {!previewMode && (
          <>
            <h2 className="h5 mb-0 section-title">Product Benefits 02</h2>
            {!isLoading && (
              isEditing ? (
                <button className="btn btn-dark btn-sm" onClick={handleSave}>Done</button>
              ) : (
                <div className="d-flex gap-2">
                  <BaseButton
                    className="outlineprimary"
                    children="Regenerate Text"
                    imageSrc="/regenerate-text.svg"
                    isModalOpen={true}
                    handleClick={onRegenerate}
                  />
                  <BaseButton
                    className="outlineprimary"
                    children="Edit Text"
                    imageSrc="/Edit.svg"
                    handleClick={toggleEdit}
                  />
                </div>
              )
            )}
          </>
        )}
      </div>

      {isLoading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            padding: "20px",
            border: "1px solid #f5c2c7",
            borderRadius: "10px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#fff",
          }}
        >
          <div style={{ flex: 1 }}>
            <Skeleton height={35} width="70%" style={{ marginBottom: "20px" }} />
            <Skeleton count={3} height={12} width="90%" style={{ marginBottom: "8px" }} />
            <Skeleton count={2} height={12} width="80%" />
          </div>
        </div>
      ) : (
        <>
          {isEditing && !previewMode ? (
            <div className="sectionbgc p-3 rounded">
              <div className="col-12">
                <div className="mb-4">
                  <span className="benefitText">Headline</span>
                  <input
                    type="text"
                    className="form-control"
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value)}
                  />
                </div>
                
                <div className="mb-4">
                  <label className="form-label">Benefits</label>
                  {benefits.map((benefit, index) => (
                    <div key={index} className="mb-3 p-3 border rounded">
                      <div className="mb-2">
                        <label className="form-label">Title {index + 1}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={benefit.title || ""}
                          onChange={(e) => {
                            const updatedBenefits = [...benefits];
                            updatedBenefits[index] = { ...updatedBenefits[index], title: e.target.value };
                            setBenefits(updatedBenefits);
                          }}
                        />
                      </div>
                      <div>
                        <label className="form-label">Description {index + 1}</label>
                        <textarea
                          className="form-control "
                          rows="3"
                          value={benefit.description || ""}
                          onChange={(e) => {
                            const updatedBenefits = [...benefits];
                            updatedBenefits[index] = { ...updatedBenefits[index], description: e.target.value };
                            setBenefits(updatedBenefits);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="sectionbgc p-3 rounded">
              {/* Display Headline */}
              {headline && (
                <div className="mb-4">
                  <h3 className="benifitTitleText text-center" >
                    {headline}
                  </h3>
                </div>
              )}
               {benefits.length > 0 && (
              <div className='row justify-content-center'>
              {benefits.map((benefit, index) => (
 <div className="col-4 " key={index}>
 <div className='d-flex justify-content-center flex-column align-items-center gap-2'>
  <div className="benefits-img"></div>
  <div className="benifitTitleText text-center">{benefit.title}</div>
  <p className="description-text productDescription text-center">{benefit.description}</p>
 </div>
 </div>
               ))}
              
              </div>
               )}
            
            </div>
          )}
        </>
      )}
    </div>
    
  );
};
export default ProductBenefit02;